import React from 'react';
import { FormLabel, RadioGroup, FormControl, Grid, TextField, FormControlLabel, Radio, FormGroup, Checkbox } from '@mui/material';
import { Event } from '../../../services/Types';
import { usePermissions } from '../../../hooks/usePermissions';
import { RadioButton } from '../../../components/RadioButton/RadioButton';
import { EventReviewProps } from '../EventReview';
import { useEventValidation } from '../../../contexts/EventValidationContext';
import { isPodcastEnabled } from '../../../config/featureFlags';

export const FeaturesAndRequestsReview: React.FC<EventReviewProps> = ({ eventData, handleEventChange }) => {
  const { eventIsUnlocked, eventIsWebinar, eventIsWebcast, eventIsPodcast } = usePermissions(eventData || {} as Event);
  const { isFieldEnabled } = useEventValidation();
  const PUBLIC_OR_PRIVATE = [
    'Public - All resources and materials to be posted to WorkforceGPS  (Common - most TA should be made public).',
    'Private - All recordings and materials to be made public after the live event concludes.',
    "Private -  all materials to remain private after the event (Uncommon, please explain in the 'Additional requests, Shutterstock ID#'s, notes, and/or instructions for your event' section below)."
  ]

  const LOGISTIC_NEEDS = [
    'Audience Polling Questions.',
    'Screen share/website demo.',
    'Closed/private chat windows – only the moderators and speakers can see what the audience has typed.',
    'Other - please explain in the additional requests input below.'
  ]

  const SUPPORT_NEEDED = [
    'Breakout rooms (Please indicate number below, up to 3)',
    'Allow attendees to unmute themselves for specific periods (interactive discussions or Q&A)',
    'Allow Attendees ability to turn on webcams',
    'American Sign Language Interpreter',
    'Other - please explain in the additional requests input below.'
  ]

  return (
    <Grid container spacing={2}>
      {/* 1. Public or Private - matches wizard order */}
      <Grid item xs={6}>
        <FormControl>
          <FormLabel>Public or Private</FormLabel>
          <RadioGroup
            name='publicOrPrivate'
            value={eventData?.publicOrPrivate}
            onChange={handleEventChange('publicOrPrivate')}
          >
            {PUBLIC_OR_PRIVATE.map(x => (
              <FormControlLabel key={x} value={x} control={<Radio disabled={!isFieldEnabled(!eventIsUnlocked)} />} label={x} disabled={!isFieldEnabled(!eventIsUnlocked)} />
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>
      {/* 2. Virtual Features / Logistic Needs - for Webinar */}
      { eventIsWebinar && (
      <Grid item xs={6}>
       <FormControl>
          <FormLabel>Logistic Needs</FormLabel>
          <FormGroup>
            {LOGISTIC_NEEDS.map(x => (
              <FormControlLabel
                key={x}
                control={
                  <Checkbox
                    checked={eventData?.logisticNeed?.includes(x) || false}
                    disabled={!isFieldEnabled(!eventIsUnlocked)}
                    onChange={(e) => {
                      const logisticNeed = JSON.parse(eventData?.logisticNeed || '[]');
                      const currentLogistic = logisticNeed;
                      const newLogistic = e.target.checked
                        ? [...currentLogistic, x]
                        : currentLogistic.filter((item: string) => item !== x);

                      const syntheticEvent = {
                        target: { value: JSON.stringify(newLogistic) }
                      } as React.ChangeEvent<HTMLInputElement>;

                      handleEventChange('logisticNeed')(syntheticEvent);
                    }}
                  />
                }
                label={x}
              />
            ))}
          </FormGroup>
        </FormControl>
      </Grid>
      )}
      {/* 3. Transcription - for Webcast/Podcast */}
      { (eventIsWebcast || (isPodcastEnabled && eventIsPodcast)) && (
        <Grid item xs={6}>
          <FormControl>
            <FormLabel>Transcription</FormLabel>
            <RadioGroup
              name='featuresTranscription'
              value={eventData?.featuresTranscription}
              onChange={handleEventChange('featuresTranscription')}
            >
              <FormControlLabel value='Yes' control={<Radio disabled={!isFieldEnabled(!eventIsUnlocked)} />} label='Yes' disabled={!isFieldEnabled(!eventIsUnlocked)} />
              <FormControlLabel value='No' control={<Radio disabled={!isFieldEnabled(!eventIsUnlocked)} />} label='No, I will be providing a script' disabled={!isFieldEnabled(!eventIsUnlocked)} />
            </RadioGroup>
          </FormControl>
        </Grid>
      )}
      {/* 4. Special Requests - for Webinar */}
      { eventIsWebinar && (
      <Grid item xs={6}>
       <FormControl>
          <FormLabel>Support Needed</FormLabel>
          <FormGroup>
            {SUPPORT_NEEDED.map((x, index) => {
              if (eventData?.overHundredParticipants && index < 3)
                return <></>
              else
                return (
              <FormControlLabel
                key={x}
                control={
                  <Checkbox
                    checked={eventData?.supportNeeded?.includes(x) || false}
                    disabled={!isFieldEnabled(!eventIsUnlocked)}
                    onChange={(e) => {
                      const supportNeeded = JSON.parse(eventData?.supportNeeded || '[]');
                      const newSupport = e.target.checked
                        ? [...supportNeeded, x]
                        : supportNeeded.filter((item: string) => item !== x);

                      const syntheticEvent = {
                        target: { value: JSON.stringify(newSupport) }
                      } as React.ChangeEvent<HTMLInputElement>;

                      handleEventChange('supportNeeded')(syntheticEvent);
                    }}
                  />
                }
                label={x}
              />
              )
            })}
          </FormGroup>
        </FormControl>
      </Grid>
      )}
      {/* 5. Additional Features & Requests */}
      <Grid item xs={6}>
        <TextField
          fullWidth
          multiline
          rows={8}
          label="Additional Features & Requests"
          value={eventData?.additionalRequest || ''}
          onChange={handleEventChange('additionalRequest')}
          disabled={!isFieldEnabled(!eventIsUnlocked)}
        />
      </Grid>
    </Grid>
  );
}; 